import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import { useSaveHolidayMutation } from '../../services/settings.service';

const { Option } = Select;

const HolidayModal = ({ visible, holiday, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [saveHoliday, { isLoading }] = useSaveHolidayMutation();

  useEffect(() => {
    if (visible) {
      if (holiday) {
        // Editing existing holiday or adding with pre-selected date
        form.setFieldsValue({
          name: holiday.name || '',
          description: holiday.description || '',
          date: holiday.date ? dayjs(holiday.date) : null,
          type: holiday.type || 'public',
          location: holiday.location || '',
        });
      } else {
        // Adding new holiday - set defaults
        form.setFieldsValue({
          name: '',
          description: '',
          date: null,
          type: 'public',
          location: '',
        });
      }
    }
  }, [visible, holiday, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        name: values.name,
        description: values.description,
        date: values.date.format('YYYY-MM-DD'),
        type: values.type,
        location: values.location,
        ...(holiday?.id && { id: holiday.id }),
      };

      await saveHoliday(payload).unwrap();

      message.success(
        `Holiday ${holiday?.id ? 'updated' : 'created'} successfully`
      );

      form.resetFields();
      onSuccess();
    } catch (error) {
      if (error?.data?.message) {
        message.error(error.data.message);
      } else if (error?.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        message.error('Failed to save holiday');
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={holiday?.id ? 'Edit Holiday' : 'Add Holiday'}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={isLoading}
      width={600}
      okText={holiday?.id ? 'Update' : 'Create'}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="name"
          label="Holiday Name"
          rules={[
            { required: true, message: 'Please enter holiday name' },
            { min: 3, message: 'Name must be at least 3 characters' },
            { max: 100, message: 'Name cannot exceed 100 characters' },
          ]}
        >
          <Input
            placeholder="e.g., Christmas, New Year, Independence Day"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="date"
          label="Date"
          rules={[
            { required: true, message: 'Please select holiday date' },
          ]}
        >
          <DatePicker
            style={{ width: '100%' }}
            size="large"
            format="DD MMM YYYY"
          />
        </Form.Item>

        <Form.Item
          name="type"
          label="Holiday Type"
          rules={[
            { required: true, message: 'Please select holiday type' },
          ]}
        >
          <Select size="large" placeholder="Select holiday type">
            <Option value="public">Public Holiday</Option>
            <Option value="optional">Optional Holiday</Option>
            <Option value="restricted">Restricted Holiday</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="location"
          label="Location"
          tooltip="Leave empty for all locations"
        >
          <Input
            placeholder="e.g., Mumbai, Delhi (optional - leave empty for all)"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 500, message: 'Description cannot exceed 500 characters' },
          ]}
        >
          <Input.TextArea
            placeholder="Brief description of the holiday (optional)"
            rows={3}
            size="large"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default HolidayModal;
